import { WebSocketCallbackData, WebSocketResponseMessage } from '@type/web-socket';
import { useRef, useCallback } from 'react';

/** Types */
export type WebSocketData = {
  title?: string,
  language?: Locale,
  category?: string,
  format?: string,
} & Record<string, string | number | undefined>;


/**
 * @description Custom hook to handle WebSocket LLM connection.
 *
 * **Returns**
 * - `isFullWsUrl`: Check if WebSocket URL is a full URL (starts with ws:// or wss://).
 * - `closeWSConnection`: Close LLM WebSocket connection.
 * - `talkWithWS`: Generic function to send message to server and listen to response.
 */
export const useLLMWebSocket = () => {
  /** Constants */
  const webSocket = useRef<WebSocket | null>(null);
  const messageString = useRef<string>('');

  /**
   * @description Check if WebSocket URL is a full URL (starts with ws:// or wss://).
   * - `url`: URL to check
   */
  const isFullWsUrl = useCallback(
    (url?: string) => url?.startsWith('ws://') || url?.startsWith('wss://') || false,
    [],
  );

  /**
   * @description Check if WebSocket connection is closed.
   */
  const isWSConnectionClosed = useCallback(
    () => !webSocket.current || [WebSocket.CLOSED as number, WebSocket.CLOSING as number].includes(webSocket.current.readyState),
    [],
  );

  /**
   * @description Close WebSocket connection.
   */
  const closeWSConnection = useCallback(() => {
    if (
      webSocket.current
      && ![WebSocket.CONNECTING as number, WebSocket.CLOSING as number, WebSocket.CLOSED as number].includes(webSocket.current.readyState)
    ) {
      webSocket.current.close();
      webSocket.current = null;
    }
    messageString.current = '';
  }, []);

  /**
   * @description Listen to messages from server.
   * - `callback`: Callback function to handle response from server. It should accept a single argument of type `WebSocketCallbackData`.
   */
  const listenToMessages = useCallback((callback: (data: WebSocketCallbackData) => void) => {
    if (!webSocket.current) {
      return;
    }
    // Receive message from server word by word. Display the words as they are received.
    webSocket.current.onmessage = (event) => {
      const data = JSON.parse(event.data as string) as WebSocketResponseMessage;
      if (data.sender === 'bot') {
        if (data.type === 'stream') {
          messageString.current += data.message;
          callback({ message: messageString.current, isSuccess: true });
        }

        if (data.type === 'start') {
          messageString.current = '';
          callback({ isStart: true });
        }

        if (data.type === 'end') {
          callback({ message: messageString.current, isEnd: true });
          messageString.current = '';
        }

        if (data.type === 'error') {
          callback({ isError: true });
          messageString.current = '';
        }
      }
    };
  }, []);


  /**
   * @description Listen to any errors from server.
   * - `callback`: Callback function to handle response from server. It should accept a single argument of type `WebSocketCallbackData`.
   */
  const listenToErrors = useCallback((callback: (data: WebSocketCallbackData) => void) => {
    if (!webSocket.current) {
      return;
    }
    webSocket.current.onerror = () => {
      callback({ isError: true });
      closeWSConnection();
    };
  }, [closeWSConnection]);


  /**
   * @description Initialize WebSocket connection to a specific URL.
   * - `url`: URL to connect to
   * - `callback`: Callback function to handle response from server. It should accept a single argument of type `WebSocketCallbackData`.
   */
  const initializeWSConnection = useCallback((url: string, callback: (data: WebSocketCallbackData) => void) => {
    if (isWSConnectionClosed()) {
      webSocket.current = new WebSocket(url);
    }
    listenToMessages(callback);
    listenToErrors(callback);
  }, [isWSConnectionClosed, listenToMessages, listenToErrors]);


  /**
   * @description  Generic function to send message to server and listen to response.
   */
  const talkWithWS = useCallback((
    url: string,
    data?: WebSocketData,
    callback?: (data: WebSocketCallbackData) => void,
  ) => {
    // Short-circuit. Check if data and callback are provided
    if (!data || !callback) {
      return;
    }

    try {
      // init connection if not already initialized
      if (isWSConnectionClosed()) {
        initializeWSConnection(url, callback);
      }
      // send message when connection is open
      if (webSocket.current && webSocket.current.readyState === WebSocket.OPEN) {
        webSocket.current.send(JSON.stringify(data));
      } else {
        webSocket.current?.addEventListener('open', () => {
          webSocket.current?.send(JSON.stringify(data));
        });
      }
    } catch (e) {
      console.error('WebSocket', e);
      callback({ message: '', isError: true });
      closeWSConnection();
    }
  }, [closeWSConnection, initializeWSConnection, isWSConnectionClosed]);


  // Return functions
  // *************************
  return {
    isFullWsUrl,
    closeWSConnection,
    talkWithWS,
  };
};
